/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react'
import { Box, Grid2, Typography, Paper, Avatar, Badge, Chip, Tooltip, Stack } from '@mui/material'
import { EmojiEvents, TouchApp, MedicalServices, SportsHockey } from '@mui/icons-material'
import CircularContentLoader from 'components/circular-content-loader/CircularContentLoader'
import PlayerImage from 'components/shared-components/PlayerImage'
import ConnectYahooFantasyTeam from 'components/shared-components/ConnectYahooAccount'
import { useHomeData } from '../hooks/useHomeData'
import InteractionHint from 'components/shared-components/InteractionHint'
import { isMobile } from 'react-device-detect'
import NoDataMessageComponent from 'components/app-no-data/AppNoData'
import { FcHighPriority } from 'react-icons/fc'

const getTrophyColor = (index: number): string => {
  switch (index) {
    case 0:
      return '#FFD700' // Gold
    case 1:
      return '#C0C0C0' // Silver
    case 2:
      return '#CD7F32' // Bronze
  }
  return '#FFFFFF' // White for others
}

const getPlayerPositionFromAbbreviation = (abbreviation: string | undefined): string => {
  switch (abbreviation?.toUpperCase()) {
    case 'C':
      return 'Center'
    case 'L':
      return 'Left Wing'
    case 'R':
      return 'Right Wing'
    case 'D':
      return 'Defenseman'
    case 'G':
      return 'Goalie'
    default:
      return abbreviation ?? 'N/A'
  }
}

interface Props {
  isEditing: boolean
}

const TopFantasyPlayers: React.FC<Props> = ({ isEditing }) => {
  const { isConnected, isLoading, topPlayers } = useHomeData(isEditing, isMobile ? 3 : 4)

  const playerList = React.useMemo(() => {
    const players = (topPlayers?.data ?? []) as Array<{
      id?: string
      name: string
      teamLogoUrl?: string
      playerHeadshotUrl?: string
      teamName?: string
      team?: string
      points?: number
      fantasyScore?: number
      position?: string
      injuryStatus?: string
    }>

    if (players.length === 0) {
      return (
        <Box sx={{
          display: 'grid',
          flexDirection: { xs: 'column', sm: 'column', md: 'column' },
          alignItems: 'center',
          width: '100%',
          gap: 1
        }}>
          <NoDataMessageComponent message="No player data available at this time" icon={FcHighPriority({ size: 'calc(36px + 1vw)' })} />
        </Box>
      )
    }

    return players.map((player, index) => {
      return (
        <Grid2 key={player.id} size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
          <Paper
            elevation={3}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 2,
              my: 2,
              borderRadius: 2,
              transition: 'transform 180ms ease, box-shadow 180ms ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) => theme.shadows[6]
              },
              '&:before': {
                content: "''",
                position: 'absolute',
                right: isMobile ? '0%' : '40%',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '100%',
                height: '100%',
                backgroundImage: `url(${player.teamLogoUrl ?? ''})`,
                backgroundSize: 'contain',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                opacity: 0.15,
                zIndex: -100,
                pointerEvents: 'none'
              }
            }}
          >
            <Box sx={{
              display: 'grid',
              flexDirection: { xs: 'column', sm: 'column', md: 'column' },
              alignItems: 'center',
              width: '100%',
              gap: 1
            }}>

              <Box ml={2} sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'row', md: 'row' }, alignItems: 'center', gap: 1, flex: 2, minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} color="text.primary" noWrap>
                  {player.name}
                </Typography>
                |
                <Tooltip title={getPlayerPositionFromAbbreviation(player.position) ?? 'Position'}>
                  <Chip
                    size="small"
                    label={getPlayerPositionFromAbbreviation(player.position) ?? 'N/A'}
                    color='primary'
                  />
                </Tooltip>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', width: { xs: '100%', sm: 'auto' } }}>
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  badgeContent={
                    <Avatar sx={{ bgcolor: getTrophyColor(index), width: 28, height: 28 }}>
                      <EmojiEvents sx={{ color: '#111', fontSize: 18 }} />
                    </Avatar>
                  }
                >
                  <PlayerImage src={player.playerHeadshotUrl} />
                </Badge>
                <Box ml={2} sx={{ flex: 1, minWidth: 0 }}>
                  <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                    <Typography variant="caption" color="text.secondary" ml={1}>
                      {typeof player.points === 'number' ? `${player.points} pts` : ''}
                    </Typography>
                  </Box>
                </Box>
                <Box ml={2}>
                  <Box sx={{ display: 'grid', alignItems: 'center', ml: { xs: 0, sm: 2 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                    <Stack direction="column" spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      <Box display="grid" alignItems="flex-start" gap={2} sx={{ mt: 0.5 }}>
                        <Tooltip title="Yahoo points" arrow>
                          <Paper sx={{ px: 2, py: 0.5, borderRadius: 2, textAlign: 'center' }} elevation={1}>
                            <Typography variant="subtitle2" fontWeight={800} color="text.primary">
                              {typeof player.fantasyScore === 'number' ? player.fantasyScore.toFixed(2) : (typeof player.points === 'number' ? player.points : '-')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">pts</Typography>
                          </Paper>
                        </Tooltip>
                        <Stack direction="column" sx={{ display: 'grid', alignItems: 'center' }} spacing={0.5}>
                          <Tooltip title={player.injuryStatus ?? 'No injury reported'}>
                            <Chip
                              size="small"
                              icon={<MedicalServices />}
                              label={player.injuryStatus ?? 'Healthy'}
                              color={typeof player.injuryStatus === 'string' && player.injuryStatus.length > 0 && player.injuryStatus.toLowerCase() !== 'healthy' ? 'error' : 'success'}
                              sx={{ borderRadius: 1 }}
                            />
                          </Tooltip>
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid2>
      )
    })
  }, [isLoading, topPlayers?.data])

  if (isLoading) {
    return (
      <Box>
        <CircularContentLoader size={'36px'} thickness={4} marginBottom={'16px'} marginTop={'16px'} textAlignment={'center'} />
      </Box>
    )
  }

  if (!(isEditing ?? false)) {
    return <InteractionHint icon={<TouchApp sx={{ fontSize: 'calc(0.5dvw + 48px)', color: 'text.secondary' }} />} />
  }

  if (!isConnected) {
    return <ConnectYahooFantasyTeam />
  }

  if (topPlayers.isLoading) {
    return (
      <Box>
        <CircularContentLoader size={'36px'} thickness={4} marginBottom={'16px'} marginTop={'16px'} textAlignment={'center'} />
      </Box>
    )
  }

  return (
    <Box>
      <Grid2 key={'top-players-list'} container spacing={1}>
        {playerList}
      </Grid2>
    </Box>
  )
}

export default React.memo(TopFantasyPlayers)
